import React, { FC } from 'react'

import { Box, Typography, useTheme } from '@mui/material'
import { isEmpty } from 'lodash'
import orderBy from 'lodash/orderBy'
import { useTranslation } from 'react-i18next'

import { StyledSwitch } from '../../../components/CustomSwitch'
import { HorizontalBarItemData, HorizontalMultiBar } from '../../../components/HorizontalMultiBar'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { SortDirection } from '../../../types'
import { getJournalingOptionTranslationKey } from '../../TradingLog/tradingLogLabels'
import { EmptyData } from '../EmptyData'
import { updateCommentsStatus } from '../ReportsSlice'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'
import { ChartOfCommentsType } from '../types'

export const ChartOfCommentsWidget: FC<Omit<ChartOfCommentsType, 'isPositive'>> = ({
  positive,
  negative,
}) => {
  const { t } = useTranslation()
  const { palette } = useTheme()
  const dispatch = useAppDispatch()
  const {
    psychology: { chartOfComments },
  } = useAppSelector(({ reports }) => reports)

  const isChecked = chartOfComments?.isPositive
  const maxNumberOfItemsPerWidget = 6
  const getCommentLabel = (name: string) =>
    t(
      `tradingLogPage.journaling.options.${getJournalingOptionTranslationKey(name)}`,
      name,
    ) as string

  const normalizedPositiveData = orderBy(positive, 'count', SortDirection.desc).slice(
    0,
    maxNumberOfItemsPerWidget,
  )

  const normalizedNegativeData = orderBy(negative, 'count', SortDirection.desc).slice(
    0,
    maxNumberOfItemsPerWidget,
  )

  const isShowEmptyWidget = (isChecked && isEmpty(negative)) || (!isChecked && isEmpty(positive))

  const barData: HorizontalBarItemData[] = (
    isChecked ? normalizedNegativeData : normalizedPositiveData
  ).map(({ name, count }) => ({
    parameter: getCommentLabel(name),
    value: count,
  }))

  const handleToggle = () => dispatch(updateCommentsStatus(!isChecked))

  return (
    <StyledWidgetWrapper>
      <ReportWidgetsHeader
        text={t('chartOfComments', 'Chart of comments')}
        explanation={t(
          'theGraphRepresentsWhatAreTheMostFrequentlySelectedComments',
          'The graph represents what are the most frequently selected comments',
        )}
      >
        <Box display="flex" flexDirection={{ xs: 'column', lg: 'row' }} gap={{ xs: 1, lg: 3 }}>
          <Typography fontWeight={500} color={isChecked ? palette.grey[700] : 'unset'}>
            {t('positive', 'Positive')}
          </Typography>
          <StyledSwitch checked={isChecked} onChange={handleToggle} />
          <Typography fontWeight={500} color={!isChecked ? palette.grey[700] : 'unset'}>
            {t('negative', 'Negative')}
          </Typography>
        </Box>
      </ReportWidgetsHeader>
      <Box mt={4}>
        {isShowEmptyWidget ? (
          <EmptyData
            title={t('notEnoughData', 'Not enough data')}
            subtitle={t('pleaseCompleteJournalingSection', 'Please complete "Journaling" section')}
          />
        ) : (
          <HorizontalMultiBar
            data={barData}
            mainColor={isChecked ? palette.red[400] : palette.green[400]}
          />
        )}
      </Box>
    </StyledWidgetWrapper>
  )
}
